<template>
  <div>
    <!-- Top row -->
    <v-row>
      <v-col cols="12">
        <v-row>
          <v-col cols="4">
            <v-btn color="primary" @click="toggleShowSettings">
              {{ showSettingsText }}
            </v-btn>
          </v-col>

          <!-- Available fields -->
          <v-col
            cols="8"
            class="drag-area my-2"
            :class="dragAreaClass"
            v-if="showSettings"
          >
            <v-col cols="12">Available fields</v-col>

            <draggable
              v-model="internal.availableFieldKeys"
              group="fields"
              @start="dragStart"
              @end="dragEnd"
            >
              <v-col cols="12">
                <v-row>
                  <v-col
                    cols="3"
                    v-for="key in internal.availableFieldKeys"
                    :key="key"
                  >
                    <field-label
                      :field="fieldsWithValues[key]"
                      v-model="fieldValues[key]"
                    >
                    </field-label>
                  </v-col>
                </v-row>
              </v-col>
            </draggable>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row v-if="showSettings">
      <v-spacer></v-spacer>

      <!-- Column fields -->
      <v-col cols="8" class="drag-area my-2" :class="dragAreaClass">
        <v-col cols="12">Columns</v-col>

        <draggable
          class="d-flex flex-row drag-area-zone"
          v-model="internal.colFieldKeys"
          group="fields"
          @start="dragStart"
          @end="dragEnd"
        >
          <v-col cols="12">
            <v-row>
              <v-col cols="3" v-for="key in internal.colFieldKeys" :key="key">
                <field-label
                  :field="fieldsWithValues[key]"
                  v-model="fieldValues[key]"
                >
                </field-label>
              </v-col>
            </v-row>
          </v-col>
        </draggable>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-row>
          <!-- Row fields -->
          <v-col
            cols="4"
            class="drag-area mx-2"
            :class="dragAreaClass"
            v-if="showSettings"
          >
            <v-col cols="12">Rows</v-col>

            <draggable
              class="d-flex flex-row drag-area-zone"
              v-model="internal.rowFieldKeys"
              group="fields"
              @start="dragStart"
              @end="dragEnd"
            >
              <v-col cols="12">
                <v-row>
                  <v-col
                    cols="3"
                    v-for="key in internal.rowFieldKeys"
                    :key="key"
                  >
                    <field-label
                      :field="fieldsWithValues[key]"
                      v-model="fieldValues[key]"
                    >
                    </field-label>
                  </v-col>
                </v-row>
              </v-col>
            </draggable>
          </v-col>

          <!-- Table -->
          <v-col cols="8">
            <!-- <pivot-table
              :data="items"
              :row-fields="rowFields"
              :col-fields="colFields"
              :reducer="reducer"
            >
            </pivot-table> -->
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import FieldLabel from "./FieldLabel.vue";
// import PivotTable from "./PivotTable.vue";
import Draggable from "vuedraggable";
import naturalSort from "javascript-natural-sort";

export default {
  name: "vue-pivot",
  components: { FieldLabel, Draggable },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    fields: {
      type: Array,
      default: () => [],
    },
    availableFieldKeys: {
      type: Array,
      default: () => [],
    },
    rowFieldKeys: {
      type: Array,
      default: () => [],
    },
    colFieldKeys: {
      type: Array,
      default: () => [],
    },
    reducer: {
      type: Function,
      default: (sum) => sum + 1,
    },
    noDataWarningText: {
      type: String,
      default: "No data to display.",
    },
    isDataLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      internal: {
        availableFieldKeys: this.availableFieldKeys,
        rowFieldKeys: this.rowFieldKeys,
        colFieldKeys: this.colFieldKeys,
      },
      fieldValues: {},
      isDragging: false,
      showSettings: true,
      showSettingsText: "Show settings",
    };
  },
  created() {
    const fieldValues = {};
    this.fields
      .filter((field) => field.valueFilter)
      .forEach((field) => {
        fieldValues[field.key] = [];
      });
    this.fieldValues = fieldValues;

    this.updateFieldValues();
  },
  watch: {
    items() {
      this.updateFieldValues();
    },
  },
  computed: {
    // Fields with values extracted from data (if field has valueFilter)
    fieldsWithValues() {
      // Create object: field.key => field
      const fieldsWithValues = {};

      this.fields.forEach((field) => {
        fieldsWithValues[field.key] = field;
      });

      // Add valuesSet
      const valueFilterableFields = this.fields.filter(
        (field) => field.valueFilter
      );

      // Create valuesSet for each value filterable field
      valueFilterableFields.forEach((field) => {
        fieldsWithValues[field.key].valuesSet = new Set();
      });

      // Iterate on data once
      this.items.forEach((item) => {
        valueFilterableFields.forEach((field) => {
          fieldsWithValues[field.key].valuesSet.add(field.getter(item));
        });
      });

      // Creates values sorted from valuesSet
      valueFilterableFields.forEach((field) => {
        fieldsWithValues[field.key].values = Array.from(
          fieldsWithValues[field.key].valuesSet
        ).sort(field.sort || naturalSort);
      });

      return fieldsWithValues;
    },
    // Fields selected values as set
    valuesFiltered: function () {
      const valuesFiltered = {};

      for (let [key, valuesObject] of Object.entries(this.fieldValues)) {
        valuesFiltered[key] = new Set();
        valuesObject.forEach((valueObject) => {
          if (valueObject.checked) {
            valuesFiltered[key].add(valueObject.value);
          }
        });
      }

      return valuesFiltered;
    },
    // Pivot table props from Pivot props & data
    rowFields: function () {
      const rowFields = [];

      this.internal.rowFieldKeys.forEach((key) => {
        const field = this.fields.find((field) => field.key === key);

        // Generate headerSlotNames from headers
        if (field.headers) {
          field.headerSlotNames = field.headers
            .filter((header) => header.checked)
            .map((header) => header.slotName);
        }

        // Add selected values
        if (field.valueFilter) {
          field.valuesFiltered = this.valuesFiltered[field.key];
        }

        rowFields.push(field);
      });

      return rowFields;
    },
    colFields: function () {
      const colFields = [];

      this.internal.colFieldKeys.forEach((key) => {
        const field = this.fields.find((field) => field.key === key);

        // Generate headerSlotNames from headers
        if (field.headers) {
          field.headerSlotNames = field.headers
            .filter((header) => header.checked)
            .map((header) => header.slotName);
        }

        // Add selected values
        if (field.valueFilter) {
          field.valuesFiltered = this.valuesFiltered[field.key];
        }

        colFields.push(field);
      });

      return colFields;
    },
    dragAreaClass() {
      return this.isDragging ? "drag-area-highlight" : null;
    },
  },
  methods: {
    // Toggle settings
    toggleShowSettings() {
      this.showSettings = !this.showSettings;
      this.showSettingsText = this.showSettings
        ? "Hide Settings"
        : "Show Settings";
    },
    dragStart() {
      this.isDragging = true;
    },
    dragEnd() {
      this.isDragging = false;
    },
    // Update fieldValues
    updateFieldValues() {
      for (let [key, field] of Object.entries(this.fieldsWithValues)) {
        if (field.valueFilter) {
          this.fieldValues[key] = [];
          field.values.forEach((value) => {
            this.fieldValues[key].push({ value, checked: true });
          });
        }
      }
    },
  },
};
</script>

<style lang="scss">
.drag-area-highlight {
  background-color: #f3f3f3;
}

.drag-area {
  border: 1px dashed #ccc;
}
</style>
